import { Menu, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import {
  IconBellOff,
  IconBellRinging,
  IconLink,
  IconMapPin,
  IconUserCheck,
  IconUserOff,
} from "@tabler/icons-react"
import { AreYouSureModal } from "@tfg/ui"
import { useEffect, useState } from "react"

import { useCreateCountryContact } from "~/pages/Admin/Organizations/hooks"
import { useGenerateMagicLinkMutation } from "~/pages/Admin/Organizations/hooks/generateMagicLinkHook"
import { useToggleUserActiveStatusMutation } from "~/pages/Admin/Organizations/hooks/useToggleUserStatusHook"
import { useUpdateUser as useMuteRemindersMutation } from "~/pages/Brand/Suppliers/List/muteRemindersHook.ts"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

import {
  invalidateOrganizationQueries,
  UserOptionsMenu,
  type UserOptionsUser,
} from "./user-options.shared"

type SupplierUserOptionsUser = UserOptionsUser & {
  /** Supplier user's own organization — never the brand page org. */
  readonly organizationId: string
}

type SupplierUserOptionsProps = {
  /** Brand organization on the admin page — used for cache invalidation only. */
  readonly brandOrganization: { readonly id: string }
  readonly user: SupplierUserOptionsUser
}

export const SupplierUserOptions = ({
  brandOrganization,
  user,
}: SupplierUserOptionsProps) => {
  const { mutateAsync: toggleUserStatus } = useToggleUserActiveStatusMutation()
  const { mutate: generateMagicLink, isPending: isGeneratingMagicLink } =
    useGenerateMagicLinkMutation()
  const { mutate: createCountryContact, isPending: isCreatingCountryContact } =
    useCreateCountryContact([user.email])

  const [hasMutedReminders, setHasMutedReminders] = useState(
    user.hasMutedReminders ?? false
  )

  useEffect(() => {
    setHasMutedReminders(user.hasMutedReminders ?? false)
  }, [user.hasMutedReminders])

  const { mutate: updateMuteReminders } = useMuteRemindersMutation({
    onSuccess: () => {
      setHasMutedReminders((previous) => !previous)
      showSuccessNotification({
        message: hasMutedReminders ? "Reminders unmuted" : "Reminders muted",
      })
      invalidateOrganizationQueries(brandOrganization.id)
    },
  })

  const [toggleOpened, { open: openToggle, close: closeToggle }] =
    useDisclosure(false)
  const [
    countryContactOpened,
    { open: openCountryContact, close: closeCountryContact },
  ] = useDisclosure(false)

  const handleConfirmToggle = async (): Promise<void> => {
    await toggleUserStatus({
      data: {
        active: !user.active,
        name: user.name,
        email: user.email,
        organizationId: user.organizationId,
        preferedLangageIso6391: user.locale,
      },
      id: user.id,
    })
    invalidateOrganizationQueries(brandOrganization.id)
    closeToggle()
  }

  const toggleStatusLabel = user.active ? "Deactivate user" : "Activate user"
  const toggleStatusIcon = user.active ? (
    <IconUserOff size={16} />
  ) : (
    <IconUserCheck size={16} />
  )

  return (
    <>
      <AreYouSureModal
        opened={toggleOpened}
        onClose={closeToggle}
        onConfirm={handleConfirmToggle}
        areYouSureText={`Are you sure you want to ${user.active ? "deactivate" : "activate"} ${user.name}? This turns ${user.active ? "off" : "on"} login access only — it does not delete supplier data or requests.`}
        confirmText={user.active ? "Deactivate" : "Activate"}
      />
      <AreYouSureModal
        opened={countryContactOpened}
        onClose={closeCountryContact}
        onConfirm={() => {
          closeCountryContact()
          createCountryContact()
        }}
        areYouSureText={`Create country contact for ${user.email}? It can be hard to roll back.`}
        confirmText="Yes, create"
      />
      <UserOptionsMenu userName={user.name}>
        <Menu.Item
          leftSection={toggleStatusIcon}
          onClick={openToggle}
          data-user-email={user.email}
          data-testid={user.active ? "deactivate-user" : "activate-user"}
        >
          <Text>{toggleStatusLabel}</Text>
        </Menu.Item>
        <Menu.Item
          leftSection={
            hasMutedReminders ? (
              <IconBellOff size={16} />
            ) : (
              <IconBellRinging size={16} />
            )
          }
          onClick={() =>
            updateMuteReminders({
              id: user.id,
              hasMutedReminders: !hasMutedReminders,
            })
          }
        >
          <Text>
            {hasMutedReminders ? "Unmute reminders" : "Mute reminders"}
          </Text>
        </Menu.Item>
        <Menu.Item
          leftSection={<IconLink size={16} />}
          onClick={() => generateMagicLink({ userId: user.id })}
          disabled={isGeneratingMagicLink}
        >
          <Text>Generate magic link</Text>
        </Menu.Item>
        <Menu.Item
          leftSection={<IconMapPin size={16} />}
          onClick={openCountryContact}
          disabled={isCreatingCountryContact}
          data-testid="create-country-contact"
        >
          <Text>Create country contact</Text>
        </Menu.Item>
      </UserOptionsMenu>
    </>
  )
}
